import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ScopeSectionIcon } from "./PatientConsentDataTypesView.styles";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { addDisplayName } from "../Infra/display-name";
import shieldWithHeart from "../Media/shield-with-heart.svg";
import { BoldText } from "./HXDesktopPage.styles";
import { ContentContainer, TinyText } from "./HXMobilePage.styles";
import { LoadingPlaceholder } from "./LoadingPlaceholder";
import { HealthRecordExamplesList, ScopeSectionTitle, Text, ScopeList, ScopeListItem, ScopeName, ScopeDescription, } from "./PatientConsentDataTypesView.styles";
import { PatientConsentDataTypesViewModel } from "./PatientConsentDataTypesViewModel";
import { ArrowDownDivider, FooterView } from "./PatientConsentManagerShared";
import { AccentedBackground, AccentedBackgroundIcon, PageWithFixedHeaderAndFooterContent, PatientConsentParent, TitlePreWrap, } from "./PatientConsentManagerShared.styles";
import { ScreenshotReadinessDetector } from "./ScreenshotReadinessDetector";
import { FlexColumnDiv, FlexRowDiv } from "./common.styles";
import { useViewModel } from "./useViewModel";
import { ConsentDataResourceScope, ConsentDataSensitivityScope } from "healthex-common/consentscopes.dtos";
import securityImage from "../Media/security.svg";
import geneticsImage from "../Media/genetics.svg";
import medicalImage from "../Media/medical-services.svg";
import factCheckImage from "../Media/fact-check.svg";
import { willNeverHappen } from "healthex-common/utils";
import { resourceScopeDescription, sensitivityScopeDescription } from "./DataScopeDescriptions";
export const PatientConsentDataTypesView = observer((props) => {
    const { t } = useTranslation(["patients"]);
    const model = useViewModel(PatientConsentDataTypesViewModel, props.container);
    if (model.prescreenOrEnrollmentStudy === NOT_LOADED)
        return _jsx(LoadingPlaceholder, { w: 10, h: 60 });
    if (model.prescreenOrEnrollmentFlow === null)
        return null;
    return (_jsx(ScreenshotReadinessDetector, { isScreenshotMode: model.isScreenshotMode, children: _jsxs(PageWithFixedHeaderAndFooterContent, { isHeaderAndFooterFixed: model.isHeaderAndFooterFixed, children: [_jsx(PatientConsentParent, { children: _jsxs(ContentContainer, { children: [_jsx(TitlePreWrap, { children: model.getCustomizedText("dataTypesTitle") }), model.isFullRecordMode ? (_jsx(AccentedBackground, { children: _jsxs(FlexRowDiv, { gap: 8, children: [_jsx(AccentedBackgroundIcon, { src: shieldWithHeart }), _jsxs(FlexColumnDiv, { children: [_jsx(BoldText, { children: t("Your complete health records will be available, which can include:") }), _jsxs(HealthRecordExamplesList, { children: [_jsx("li", { children: t("Contact information: Details such as your name, phone number, email, and address") }), _jsx("li", { children: t("Demographics: Details such as your age, sex, race, and ethnicity") }), _jsx("li", { children: t("Medical history: Details such as your past visits, diagnoses, treatment plans, medications, allergies, immunizations, lab results, imaging, procedures, and clinical notes") }), _jsx("li", { children: t("Family and genetic information: Details such as your family history, social history, and genomic data") }), _jsx("li", { children: t("Sensitive information: May include details such as your HIV status, STIs, substance use, reproductive health, disabilities, and other protected health information") })] })] })] }) })) : (_jsxs(_Fragment, { children: [_jsx(TinyText, { children: t("Scroll to see all of the types of health information we may access, if available in your records.") }), _jsx(ArrowDownDivider, {}), model.scopesSections.map((section) => (_jsxs(FlexColumnDiv, { children: [_jsxs(FlexRowDiv, { gap: 4, children: [getScopeSectionIcon(section), _jsx(ScopeSectionTitle, { children: getSectionTitle(section) })] }), _jsx(Text, { children: t("Includes only the items listed below.") }), _jsx(ScopeList, { children: section.items.map((scope) => (_jsxs(ScopeListItem, { children: [_jsx(ScopeName, { children: getScopeName(scope) }), _jsx(ScopeDescription, { children: getScopeDescription(scope) })] }))) })] })))] }))] }) }), _jsx(FooterView, { parentModel: model, confirmButtonText: t("Continue"), onConfirmButtonClicked: () => model.goToNextPage() })] }) }));
    function getScopeSectionIcon(section) {
        switch (section.type) {
            case "BASIC_DETAILS":
                return _jsx(ScopeSectionIcon, { src: factCheckImage });
            case "MEDICAL_HISTORY":
                return _jsx(ScopeSectionIcon, { src: medicalImage });
            case "FAMILY_AND_GENETICS":
                return _jsx(ScopeSectionIcon, { src: geneticsImage });
            case "SENSITIVE_INFORMATION":
                return _jsx(ScopeSectionIcon, { src: securityImage });
        }
        return willNeverHappen(section.type);
    }
    function getSectionTitle(section) {
        switch (section.type) {
            case "BASIC_DETAILS":
                return t("Basic details");
            case "MEDICAL_HISTORY":
                return t("Medical history");
            case "FAMILY_AND_GENETICS":
                return t("Family and genetics");
            case "SENSITIVE_INFORMATION":
                return t("Sensitive information");
        }
        return willNeverHappen(section.type);
    }
    function getScopeName(scope) {
        if (scope === ConsentDataSensitivityScope.NORMAL ||
            scope === ConsentDataSensitivityScope.HIV_OR_STI ||
            scope === ConsentDataSensitivityScope.SUBSTANCE_USE_OR_DRUG_ABUSE ||
            scope === ConsentDataSensitivityScope.MENTAL_HEALTH ||
            scope === ConsentDataSensitivityScope.GENETIC_TESTING ||
            scope === ConsentDataSensitivityScope.REPRODUCTIVE_HEALTH_DATA ||
            scope === ConsentDataSensitivityScope.PSYCHOTHERAPY_NOTES ||
            scope === ConsentDataSensitivityScope.SUD_PART_2 ||
            scope === ConsentDataSensitivityScope.SICKLE_CELL) {
            return sensitivityScopeDescription(scope);
        }
        else {
            return resourceScopeDescription(scope);
        }
    }
    function getScopeDescription(scope) {
        switch (scope) {
            case ConsentDataResourceScope.CONTACT_INFO:
                return t("Your name, phone number, email, and address");
            case ConsentDataResourceScope.DEMOGRAPHICS:
                return t("Information like your age, gender, race, or ethnicity");
            case ConsentDataResourceScope.ADMINISTRATIVE_AND_BILLING:
                return t("Health insurance or billing information");
            case ConsentDataResourceScope.VISITS:
                return t("Records of appointments, checkups, or consultations");
            case ConsentDataResourceScope.DIAGNOSES:
                return t("Health conditions you’ve been diagnosed with");
            case ConsentDataResourceScope.TREATMENT_PLAN:
                return t("Planned care or recommendations from your care team");
            case ConsentDataResourceScope.MEDICATIONS:
                return t("Prescriptions you’ve taken or are taking");
            case ConsentDataResourceScope.ALLERGIES:
                return t("Known allergies to medications or substances");
            case ConsentDataResourceScope.IMMUNIZATIONS:
                return t("Vaccines you’ve received");
            case ConsentDataResourceScope.LABS:
                return t("Results from bloodwork and other lab tests");
            case ConsentDataResourceScope.IMAGING:
                return t("X-rays, MRIs, CT scans, etc.");
            case ConsentDataResourceScope.DIAGNOSTIC_REPORTS:
                return t("Summaries from tests or procedures");
            case ConsentDataResourceScope.PROCEDURES:
                return t("Medical procedures or surgeries you've had");
            case ConsentDataResourceScope.CLINICAL_VITALS:
                return t("Information like your blood pressure, or heart rate");
            case ConsentDataResourceScope.CLINICAL_NOTES:
                return t("Notes written by your doctors and care team");
            case ConsentDataResourceScope.GENOMIC_DATA:
                return t("DNA or genetic test results");
            case ConsentDataResourceScope.FAMILY_HISTORY:
                return t("Health conditions that run in your family");
            case ConsentDataResourceScope.SOCIAL_HISTORY:
                return t("Lifestyle information such as tobacco or alcohol use");
            case ConsentDataSensitivityScope.HIV_OR_STI:
                return t("Test results or treatment for sexually transmitted infections");
            case ConsentDataSensitivityScope.SUBSTANCE_USE_OR_DRUG_ABUSE:
                return t("Information about treatment for drug or alcohol use");
            case ConsentDataSensitivityScope.MENTAL_HEALTH:
                return t("Information about your mental health care (not including psychotherapy notes)");
            case ConsentDataSensitivityScope.GENETIC_TESTING:
                return t("Details from tests about your genes or inherited conditions");
            case ConsentDataSensitivityScope.REPRODUCTIVE_HEALTH_DATA:
                return t("Information about birth control, pregnancy, fertility, etc.");
            case ConsentDataSensitivityScope.SICKLE_CELL:
                return t("Information related to sickle cell disease");
            case ConsentDataSensitivityScope.NORMAL:
            case ConsentDataSensitivityScope.PSYCHOTHERAPY_NOTES:
            case ConsentDataSensitivityScope.SUD_PART_2:
                return null;
        }
        return willNeverHappen(scope);
    }
});
addDisplayName(PatientConsentDataTypesView, "PatientConsentDataTypesView");
